import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import ClerkAuthBridge from './components/ClerkAuthBridge';
import { Toaster } from './components/ui/sonner';
import { ClerkProvider, useClerk } from '@clerk/clerk-react';

// Route components are code-split so the initial bundle stays tiny — each
// screen (and heavy deps like recharts) loads only when it's actually visited.
// LandingPage + Dashboard are prefetched since they're the usual first screens.
const LandingPage = lazy(() => import(/* webpackPrefetch: true */ './components/LandingPage'));
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ './components/Dashboard'));
const CoursePage = lazy(() => import('./components/CoursePage'));
const EnhancedLessonPlayer = lazy(() => import('./components/EnhancedLessonPlayer'));
const TeacherCourseDetail = lazy(() => import('./components/TeacherCourseDetail'));
const PartByPartLessonBuilder = lazy(() => import('./components/lesson/PartByPartLessonBuilder'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ConceptLearningPlayer = lazy(() => import('./components/ConceptLearningPlayer'));
const PromptThinkingPlayer = lazy(() => import('./components/PromptThinkingPlayer'));
const BuildQuestPlayer = lazy(() => import('./components/BuildQuestPlayer'));
const StudentOnboarding = lazy(() => import('./components/StudentOnboarding'));
const CompetitionInvite = lazy(() => import('./components/CompetitionInvite'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext(null);

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
    </div>
  );
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { signOut: clerkSignOut } = useClerk();

  axios.defaults.withCredentials = true;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { console.error('Backend logout error', e); }
    try { await clerkSignOut({ redirectUrl: '/' }); } catch (e) { console.error('Clerk signOut error', e); }
    setUser(null);
    window.location.href = '/';
  };

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>
      <ClerkAuthBridge />
      <BrowserRouter>
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            <Route path="/c/:competitionId" element={<CompetitionInvite />} />
            <Route path="/teacher-login" element={user ? <Navigate to="/dashboard" /> : <LandingPage isTeacherMode={true} />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/onboarding" element={user?.role === 'student' ? <StudentOnboarding /> : <Navigate to="/dashboard" />} />
            <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
            <Route path="/concepts/:conceptId" element={user?.role === 'student' || user?.role === 'admin' ? <ConceptLearningPlayer /> : <Navigate to="/dashboard" />} />
            <Route path="/prompt-lab/:exerciseId" element={user?.role === 'student' || user?.role === 'admin' ? <PromptThinkingPlayer /> : <Navigate to="/dashboard" />} />
            <Route path="/build-quest/:questId" element={user ? <BuildQuestPlayer /> : <Navigate to="/dashboard" />} />
            <Route path="/courses/:courseId" element={user ? <CoursePage /> : <Navigate to="/" />} />
            <Route path="/courses/:courseId/manage" element={user?.role === 'teacher' ? <TeacherCourseDetail /> : <Navigate to="/dashboard" />} />
            <Route path="/courses/:courseId/part-by-part" element={user?.role === 'teacher' ? <PartByPartLessonBuilder /> : <Navigate to="/dashboard" />} />
            <Route path="/lessons/:lessonId" element={user ? <EnhancedLessonPlayer /> : <Navigate to="/" />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

function App() {
  // The Clerk publishable key is not secret. If it's provided at build time we
  // use it immediately and skip the /config round-trip (one less thing blocking
  // first paint, especially on a cold backend). Otherwise we fetch it as before.
  const buildTimeClerkKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
  const [clerkKey, setClerkKey] = useState(buildTimeClerkKey || null);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    if (buildTimeClerkKey) return; // already have it — no network needed
    axios.get(`${API}/config`)
      .then((r) => {
        const key = r.data?.clerkPublishableKey;
        if (!key) {
          setConfigError('Clerk publishable key not configured on server.');
        } else {
          setClerkKey(key);
        }
      })
      .catch((e) => {
        console.error('Config fetch failed', e);
        setConfigError('Could not load app configuration. Please refresh.');
      });
  }, [buildTimeClerkKey]);

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Configuration Error</h2>
          <p className="text-gray-700">{configError}</p>
        </div>
      </div>
    );
  }

  if (!clerkKey) {
    return <FullScreenLoader />;
  }

  return (
    <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <AppRoutes />
    </ClerkProvider>
  );
}

export default App;
